/**
 * Ask for a password reset link.
 *
 * The whole screen is arranged around one rule: **the answer must not reveal
 * whether an address has an account.** `requestPasswordReset` already resolves
 * identically for both cases, and the easiest way to throw that away is right
 * here — a cheerful "check your inbox" for one address and "we couldn't find
 * that" for another turns this form into a membership oracle for a school.
 *
 * So there is exactly one success state, its wording is conditional ("if that
 * address has an account"), and the form is replaced by it either way.
 */

import { useState, type FormEvent } from 'react';
import { Button } from '../design/Button';
import { TextField } from '../design/Field';
import { AuthLayout } from '../design/Feedback';
import { FailureSummary, SuccessPanel, useSubmission } from './formSupport';
import { useTurnstile } from './Turnstile';
import { useAuth } from './AuthProvider';
import type { Route } from './routes';

export interface ForgotPasswordProps {
  navigate: (route: Route, options?: { replace?: boolean }) => void;
}

export function ForgotPassword({ navigate }: ForgotPasswordProps) {
  const { auth } = useAuth();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const { busy, failure, submit } = useSubmission();
  const turnstile = useTurnstile();

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const ok = await submit(async () => {
      await auth.requestPasswordReset(email.trim(), {
        turnstileToken: turnstile.token ?? undefined,
      });
    });
    if (ok) setSent(true);
    else turnstile.reset();
  }

  if (sent) {
    return (
      <AuthLayout heading="Check your email">
        <SuccessPanel
          title="If that address has an account, a reset link is on its way"
          actions={
            <Button variant="secondary" onClick={() => navigate({ name: 'sign-in' })}>
              Back to sign in
            </Button>
          }
        >
          <p>
            Open the link on this iPad to choose a new password. It expires after a while, and
            asking again replaces any earlier link.
          </p>
        </SuccessPanel>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      heading="Reset your password"
      sub="We will email you a link to choose a new one."
      footer={
        <>
          Remembered it?{' '}
          <button
            type="button"
            className="mm-auth__link"
            onClick={() => navigate({ name: 'sign-in' })}
          >
            Sign in
          </button>
        </>
      }
    >
      <FailureSummary failure={failure} handledFields={['email']} />

      <form onSubmit={onSubmit} noValidate>
        <TextField
          label="Email"
          type="email"
          name="email"
          value={email}
          autoComplete="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          inputMode="email"
          required
          error={failure?.fields.email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <turnstile.Widget />

        <div className="mm-auth__actions">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            loading={busy}
            disabled={turnstile.blocking}
          >
            Send reset link
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
